import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Button,
  Typography,
  Stack,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  FitnessCenter as FitnessCenterIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import esLocale from 'date-fns/locale/es';
import DynamicPerformanceModal from 'components/performance/DynamicPerformanceModal';
import DynamicPerformanceDataGrid from 'components/performance/DynamicPerformanceDataGrid';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import AppBarContext from 'context/AppBarContext';
import performanceSlice from 'store/modules/performance';
import requestStatus from 'common/constants/status';

function DynamicPerformancePage() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['performances', 'buttons', 'labels']);
  const { setAppBarTitle, setAppBarButton } = useContext(AppBarContext);

  // Redux state
  const performances = useSelector((state) => state.performance.data.dynamicPerformances);
  const loading = useSelector((state) => state.performance.status.listDynamic === requestStatus.loading);
  const createLoading = useSelector((state) => state.performance.status.createDynamic === requestStatus.loading);
  const updateLoading = useSelector((state) => state.performance.status.updateDynamic === requestStatus.loading);
  const createSuccess = useSelector((state) => state.performance.status.createDynamic === requestStatus.success);
  const updateSuccess = useSelector((state) => state.performance.status.updateDynamic === requestStatus.success);
  const refresh = useSelector((state) => state.performance.refresh);

  // Data for clients and exercises
  const clients = useSelector((state) => state.client?.data?.all || []);
  const exercises = useSelector((state) => state.exercise?.data?.exercises || []);

  // Local state
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPerformance, setSelectedPerformance] = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    client_id: '',
    exercise_id: '',
    period_start_date: null,
    period_end_date: null
  });

  const pageTitle = t('dynamicPerformances.title', { ns: 'performances' });

  const loadPerformances = useCallback(() => {
    const activeFilters = {};
    if (filters.client_id) activeFilters.client_id = filters.client_id;
    if (filters.exercise_id) activeFilters.exercise_id = filters.exercise_id;
    if (filters.period_start_date) activeFilters.period_start_date = filters.period_start_date;
    if (filters.period_end_date) activeFilters.period_end_date = filters.period_end_date;

    dispatch(performanceSlice.actions.listDynamic(activeFilters));
  }, [dispatch, filters]);

  useEffect(() => {
    setAppBarTitle(pageTitle);
    setAppBarButton(null);
  }, [setAppBarTitle, setAppBarButton, pageTitle]);

  useEffect(() => {
    loadPerformances();
  }, [loadPerformances]);

  useEffect(() => {
    if (refresh) {
      loadPerformances();
    }
  }, [refresh, loadPerformances]);

  useEffect(() => {
    if (createSuccess || updateSuccess) {
      setModalOpen(false);
      setSelectedPerformance(null);
    }
  }, [createSuccess, updateSuccess]);

  const handleCreate = (data) => {
    dispatch(performanceSlice.actions.createDynamic(data));
  };

  const handleEdit = (performance) => {
    setSelectedPerformance(performance);
    setModalOpen(true);
  };

  const handleUpdate = (data) => {
    if (selectedPerformance) {
      dispatch(
        performanceSlice.actions.updateDynamic({
          id: selectedPerformance.id,
          input: data
        })
      );
    }
  };

  const handleDeleteClick = (performance) => {
    setSelectedPerformance(performance);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedPerformance) {
      dispatch(performanceSlice.actions.deleteDynamic(selectedPerformance.id));
      setDeleteDialogOpen(false);
      setSelectedPerformance(null);
    }
  };

  const handleApplyFilters = () => {
    loadPerformances();
    setFilterDialogOpen(false);
  };

  const handleClearFilters = () => {
    setFilters({
      client_id: '',
      exercise_id: '',
      period_start_date: null,
      period_end_date: null
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
      <SEOPageTitle title={pageTitle} />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h4" fontWeight="bold" color="text.primary">
              {pageTitle}
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setFilterDialogOpen(true)}
                sx={{
                  color: 'text.primary',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'text.disabled'
                  }
                }}
              >
                {t('dynamicPerformances.filters', { ns: 'performances' })}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadPerformances}
                sx={{
                  color: 'text.primary',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'text.disabled'
                  }
                }}
              >
                {t('dynamicPerformances.refresh', { ns: 'performances' })}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedPerformance(null);
                  setModalOpen(true);
                }}
                sx={{
                  bgcolor: '#F9BD4E',
                  color: 'text.primary',
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: '#ffd166'
                  }
                }}
              >
                {t('dynamicPerformances.newPerformance', { ns: 'performances' })}
              </Button>
            </Stack>
          </Stack>

          {/* Active Filters Display */}
          {(filters.client_id || filters.exercise_id || filters.period_start_date || filters.period_end_date) && (
            <Card sx={{ bgcolor: 'rgba(249, 189, 78, 0.1)', border: '1px solid rgba(249, 189, 78, 0.3)' }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" color="text.primary" fontWeight="bold">
                    {t('dynamicPerformances.activeFilters', { ns: 'performances' })}
                  </Typography>
                  {filters.client_id && (
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                      {t('dynamicPerformances.table.client', { ns: 'performances' })}: {clients.find((c) => c.id === filters.client_id)?.first_name || filters.client_id}
                    </Typography>
                  )}
                  {filters.exercise_id && (
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                      {t('dynamicPerformances.table.exercise', { ns: 'performances' })}: {exercises.find((e) => e.id === filters.exercise_id)?.name || filters.exercise_id}
                    </Typography>
                  )}
                  {filters.period_start_date && (
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                      {t('dynamicPerformances.modal.periodStart', { ns: 'performances' })}: {filters.period_start_date.toLocaleDateString()}
                    </Typography>
                  )}
                  {filters.period_end_date && (
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                      {t('dynamicPerformances.modal.periodEnd', { ns: 'performances' })}: {filters.period_end_date.toLocaleDateString()}
                    </Typography>
                  )}
                  <Button
                    size="small"
                    onClick={handleClearFilters}
                    sx={{ color: '#f44336', ml: 'auto' }}
                  >
                    {t('dynamicPerformances.clearFilters', { ns: 'performances' })}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Performance DataGrid */}
        <DynamicPerformanceDataGrid
          performances={performances}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          loading={loading}
        />

        {/* Create/Edit Modal */}
        <DynamicPerformanceModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedPerformance(null);
          }}
          onSubmit={selectedPerformance ? handleUpdate : handleCreate}
          performance={selectedPerformance}
          clients={clients}
          exercises={exercises}
          loading={selectedPerformance ? updateLoading : createLoading}
        />

        {/* Filter Dialog */}
        <Dialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              backgroundColor: 'surface.content', backgroundImage: 'none'
            }
          }}
        >
          <DialogTitle sx={{ color: 'text.primary', fontWeight: 'bold' }}>
            {t('dynamicPerformances.filter.title', { ns: 'performances' })}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'text.secondary' }}>
                    {t('dynamicPerformances.table.client', { ns: 'performances' })}
                  </InputLabel>
                  <Select
                    value={filters.client_id}
                    onChange={(e) => setFilters({ ...filters, client_id: e.target.value })}
                    label={t('dynamicPerformances.table.client', { ns: 'performances' })}
                    sx={{
                      color: 'text.primary',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#F9BD4E'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#F9BD4E'
                      },
                      '& .MuiSvgIcon-root': {
                        color: '#F9BD4E'
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: 'surface.content',
                          '& .MuiMenuItem-root': {
                            color: 'text.primary',
                            '&:hover': {
                              bgcolor: 'rgba(249, 189, 78, 0.1)'
                            },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(249, 189, 78, 0.2)',
                              '&:hover': {
                                bgcolor: 'rgba(249, 189, 78, 0.3)'
                              }
                            }
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem value="">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon sx={{ fontSize: 18, color: '#F9BD4E' }} />
                        <Typography>{t('dynamicPerformances.filter.allClients', { ns: 'performances' })}</Typography>
                      </Stack>
                    </MenuItem>
                    {clients?.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PersonIcon sx={{ fontSize: 18, color: '#F9BD4E' }} />
                          <Typography>{client.first_name} {client.last_name}</Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'text.secondary' }}>
                    {t('dynamicPerformances.table.exercise', { ns: 'performances' })}
                  </InputLabel>
                  <Select
                    value={filters.exercise_id}
                    onChange={(e) => setFilters({ ...filters, exercise_id: e.target.value })}
                    label={t('dynamicPerformances.table.exercise', { ns: 'performances' })}
                    sx={{
                      color: 'text.primary',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#F9BD4E'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#F9BD4E'
                      },
                      '& .MuiSvgIcon-root': {
                        color: '#F9BD4E'
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: 'surface.content',
                          '& .MuiMenuItem-root': {
                            color: 'text.primary',
                            '&:hover': {
                              bgcolor: 'rgba(249, 189, 78, 0.1)'
                            },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(249, 189, 78, 0.2)',
                              '&:hover': {
                                bgcolor: 'rgba(249, 189, 78, 0.3)'
                              }
                            }
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem value="">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <FitnessCenterIcon sx={{ fontSize: 18, color: '#F9BD4E' }} />
                        <Typography>{t('dynamicPerformances.filter.allExercises', { ns: 'performances' })}</Typography>
                      </Stack>
                    </MenuItem>
                    {exercises?.map((exercise) => (
                      <MenuItem key={exercise.id} value={exercise.id}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <FitnessCenterIcon sx={{ fontSize: 18, color: '#F9BD4E' }} />
                          <Typography>{exercise.name}</Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <DatePicker
                  label={t('dynamicPerformances.modal.periodStart', { ns: 'performances' })}
                  value={filters.period_start_date}
                  onChange={(newValue) => setFilters({ ...filters, period_start_date: newValue })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          color: 'text.primary',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' }
                        },
                        '& .MuiInputLabel-root': { color: 'text.secondary' }
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <DatePicker
                  label={t('dynamicPerformances.modal.periodEnd', { ns: 'performances' })}
                  value={filters.period_end_date}
                  onChange={(newValue) => setFilters({ ...filters, period_end_date: newValue })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          color: 'text.primary',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' }
                        },
                        '& .MuiInputLabel-root': { color: 'text.secondary' }
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setFilterDialogOpen(false)}
              sx={{ color: 'text.secondary' }}
            >
              {t('cancel', { ns: 'buttons' })}
            </Button>
            <Button
              onClick={handleApplyFilters}
              variant="contained"
              sx={{
                bgcolor: '#F9BD4E',
                color: 'text.primary',
                '&:hover': { bgcolor: '#ffd166' }
              }}
            >
              {t('dynamicPerformances.filter.apply', { ns: 'performances' })}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              backgroundColor: 'surface.content', backgroundImage: 'none'
            }
          }}
        >
          <DialogTitle sx={{ color: 'text.primary', fontWeight: 'bold' }}>
            {t('dynamicPerformances.confirmDelete', { ns: 'performances' })}
          </DialogTitle>
          <DialogContent>
            <Typography color="rgba(255, 255, 255, 0.7)">
              {t('dynamicPerformances.confirmDeleteMessage', { ns: 'performances' })}
              {' '}{t('dynamicPerformances.cannotUndo', { ns: 'performances' })}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={{ color: 'text.secondary' }}
            >
              {t('cancel', { ns: 'buttons' })}
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                bgcolor: '#f44336',
                color: 'text.primary',
                '&:hover': { bgcolor: '#d32f2f' }
              }}
            >
              {t('delete', { ns: 'buttons' })}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
}

export default DynamicPerformancePage;
